/**
 * Failure simulation menu: a popover listing the scenario catalogue with
 * each scenario's stages and a start or stop toggle, plus reset all. Writes
 * the plain scenario ids to useTwin.failureScenarios (the simulation session
 * starts and stops the controller from that list) and switches the source
 * to SIM when a scenario starts, as the previous build did.
 */
import { Popover } from 'radix-ui';
import { FlaskConical, Play, Square, Undo2, X } from 'lucide-react';
import { Chip, IconButton, type Tone } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { useTwin } from '../../store';
import { FAILURE_SCENARIOS, type ScenarioAlert } from '../adapters/scenarios';
import { Button } from '../components/layout';

const ALERT_TONE: Record<ScenarioAlert, Tone> = { INFO: 'neutral', WARNING: 'caution', CRITICAL: 'warning' };

export function FailureSimMenu({ className }: { className?: string }) {
  const active = useTwin((s) => s.failureScenarios);
  const setFailureScenarios = useTwin((s) => s.setFailureScenarios);
  const liveSource = useTwin((s) => s.liveSource);
  const setLiveSource = useTwin((s) => s.setLiveSource);

  const isActive = (id: string) => active.includes(id);
  const start = (id: string) => {
    setFailureScenarios([...active.filter((a) => a !== id), id]);
    if (liveSource === 'off') setLiveSource('sim');
  };
  const stop = (id: string) => setFailureScenarios(active.filter((a) => a !== id));

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-control border px-2 py-1 text-[11px] transition',
            active.length ? 'border-warning/50 bg-warning/10 text-warning' : 'border-hairline-strong bg-raised/80 text-ink-2 hover:bg-overlay hover:text-ink',
            className,
          )}
          title="Simulate a failure"
        >
          <FlaskConical className="h-3.5 w-3.5" />
          Simulate failure
          {active.length > 0 && <span className="font-mono">{active.length}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} className="z-50 w-[380px] rounded-panel border border-hairline-strong bg-raised p-3 text-xs shadow-elev-2 focus:outline-none">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-ink">Failure simulation</div>
            <Popover.Close asChild>
              <IconButton label="Close" className="h-6 w-6">
                <X className="h-3.5 w-3.5" />
              </IconButton>
            </Popover.Close>
          </div>
          <p className="mb-2 text-[11px] leading-snug text-ink-3">
            A scenario walks its stages on the SIM updates (8 updates by default) and raises an alert at each stage; starting one turns the SIM source on. Every reading it touches stays badged SIM.
          </p>
          <ul className="flex flex-col gap-1.5">
            {FAILURE_SCENARIOS.map((s) => {
              const running = isActive(s.id);
              return (
                <li key={s.id} className={cn('rounded-control border px-2.5 py-2', running ? 'border-warning/40 bg-warning/5' : 'border-hairline')}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-ink">
                        {s.name}
                        {running && (
                          <Chip tone="warning" pulse className="px-1.5 py-0 text-[9px]">
                            running
                          </Chip>
                        )}
                      </div>
                      <div className="text-[10px] text-ink-3">{s.description}</div>
                    </div>
                    {running ? (
                      <Button onClick={() => stop(s.id)} danger className="px-2 py-1">
                        <Square className="h-3 w-3" /> Stop
                      </Button>
                    ) : (
                      <Button onClick={() => start(s.id)} primary className="px-2 py-1">
                        <Play className="h-3 w-3" /> Start
                      </Button>
                    )}
                  </div>
                  <ol className="mt-1.5 flex flex-wrap items-center gap-1" aria-label={`${s.name} stages`}>
                    {s.stages.map((st, i) => (
                      <li key={i} title={st.message}>
                        <Chip tone={ALERT_TONE[st.alert]} className="px-1.5 py-0 text-[9px]">
                          {i + 1} {st.alert}
                        </Chip>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-1 truncate font-mono text-[10px] text-ink-3" title={s.affectedSensors.join(', ')}>
                    {s.affectedSensors.join(', ')}
                  </div>
                </li>
              );
            })}
          </ul>
          <Button onClick={() => setFailureScenarios([])} disabled={active.length === 0} className="mt-2 w-full justify-center">
            <Undo2 className="h-3.5 w-3.5" /> Reset all simulations
          </Button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
